import { BookOpen } from 'lucide-react'
import { Book, LibraryBook } from '../types'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getLanguageFlag } from '../lib/constants/languages'

interface BookCardProps {
  book: Book | LibraryBook
  className?: string
  variant?: 'user-book' | 'library-book'
  onCardClick?: () => void
  
  // Optional overlay badges (positioned at top-right of cover)
  badges?: React.ReactNode
  
  // Optional bottom overlay on cover (e.g., reading progress bar)
  bottomOverlay?: React.ReactNode
  
  // Optional action button at bottom of card info section
  actionButton?: React.ReactNode
  
  // Optional extra info row (e.g., language + date for taken books)
  extraInfo?: React.ReactNode
  
  // Optional hover action button on cover (e.g., "Read" button)
  onHoverActionClick?: (e: React.MouseEvent) => void
  hoverActionLabel?: string
  
  // Control visibility of default elements
  showLanguageFlags?: boolean
  showGenre?: boolean
}

// Type guard to check if book is LibraryBook
const isLibraryBook = (book: Book | LibraryBook): book is LibraryBook => {
  return 'title_international' in book
}

export default function BookCard({ 
  book, 
  className = '', 
  variant,
  onCardClick,
  badges,
  bottomOverlay,
  actionButton,
  extraInfo,
  onHoverActionClick,
  hoverActionLabel,
  showLanguageFlags = true,
  showGenre = true
}: BookCardProps) {
  const navigate = useNavigate()
  const { t } = useTranslation('common')
  
  // Auto-detect variant if not provided
  const cardVariant = variant || (isLibraryBook(book) ? 'library-book' : 'user-book')
  const isLibrary = cardVariant === 'library-book'

  // Extract common properties
  const title = isLibraryBook(book) ? book.title_international : book.title
  const author = isLibraryBook(book) 
    ? book.author_international 
    : (book.author || 'Unknown Author')
  const coverUrl = isLibraryBook(book) ? book.cover_url : (book as any).cover_url
  const languages = isLibraryBook(book) 
    ? book.available_languages 
    : (book.targetLanguage ? [book.targetLanguage] : [])
  const complexity = isLibraryBook(book) ? null : book.complexityLevel
  const genre = isLibraryBook(book) ? book.genre : null

  const handleClick = () => {
    if (onCardClick) {
      onCardClick()
    } else if (isLibrary) {
      navigate(`/library/books/${book.id}`)
    } else {
      // User's personal book
      navigate(`/user/books/${book.id}`)
    }
  }

  return (
    <div 
      className={`book-card group cursor-pointer ${className}`}
      onClick={handleClick}
    >
      <div className="relative">
        {/* Cover Image */}
        <div className="book-card-cover aspect-[2/3] mb-3 bg-muted rounded-sm overflow-hidden border border-border/50 relative">
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
              <BookOpen className="h-16 w-16 text-muted-foreground/30" />
            </div>
          )}
          
          {/* Badges slot (top-right corner) */}
          {badges && (
            <div className="book-card-badges absolute top-2 right-2 flex flex-col gap-1 items-end">
              {badges}
            </div>
          )}
          
          {/* Hover action button (e.g., "Read" button) - animated slide-up on hover */}
          {onHoverActionClick && (
            <div className={`hover-action-overlay absolute inset-x-0 overflow-hidden pointer-events-none ${bottomOverlay ? 'bottom-1.5' : 'bottom-0'}`}>
              {/* Gradient backdrop for better visibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent 
                              opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              {/* Button container - slides up on hover */}
              <div className="relative translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out
                              p-3 pointer-events-auto">
                <button
                  onClick={onHoverActionClick}
                  className="read-btn w-full flex items-center justify-center gap-2 
                             bg-primary hover:bg-primary/90 text-primary-foreground
                             py-2 px-4 rounded-md text-sm font-medium
                             transform hover:scale-[1.02] active:scale-[0.98]
                             transition-all duration-200"
                >
                  <BookOpen className="h-4 w-4" />
                  <span>{hoverActionLabel || t('reader.read', 'Read')}</span>
                </button>
              </div>
            </div>
          )}
          
          {/* Bottom overlay slot (e.g., progress bar) */}
          {bottomOverlay && (
            <div className="book-card-bottom-overlay absolute bottom-0 left-0 right-0">
              {bottomOverlay}
            </div>
          )}
        </div>

        {/* Book Info */}
        <div className="book-card-info space-y-1">
          {/* Title - single line with ellipsis, full title on hover */}
          <h3 
            className="font-medium text-sm truncate leading-tight text-foreground group-hover:text-primary transition-colors"
            title={title}
          >
            {title}
          </h3>

          {/* Author */}
          <p className="text-xs text-muted-foreground line-clamp-1">
            {author}
          </p>

          {/* Genre */}
          {showGenre && genre && (
            <p className="text-xs text-primary/80 font-medium">
              {genre}
            </p>
          )}

          {/* Languages/Formats */}
          {showLanguageFlags && languages.length > 0 && (
            <div className="book-card-languages flex flex-wrap gap-1 py-1 items-center">
              <span className="text-xs text-muted-foreground mr-0.5">
                {t('book.availableIn')}
              </span>
              {languages.slice(0, 3).map((lang) => (
                <span key={lang} className="text-sm" title={lang}>
                  {getLanguageFlag(lang)}
                </span>
              ))}
              {languages.length > 3 && (
                <span className="text-xs text-muted-foreground">
                  +{languages.length - 3}
                </span>
              )}
              {complexity && (
                <span className="text-xs font-medium text-muted-foreground ml-1">
                  • {complexity}
                </span>
              )}
            </div>
          )}
          
          {/* Extra info slot (e.g., language + date) */}
          {extraInfo && (
            <div className="book-card-extra-info">
              {extraInfo}
            </div>
          )}
          
          {/* Action button slot */}
          {actionButton && (
            <div className="book-card-action mt-2">
              {actionButton}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
